import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Typography,
  Alert,
  Autocomplete,
  Chip,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import metadataService, {
  SourceFieldFilter,
  FilterCondition,
  FilterOperator,
  RelatedFieldDefinition,
} from '../../../services/metadataService';

interface SourceFieldFilterBuilderProps {
  sourceFielddefId: string;
  tenantId: string;
  value: SourceFieldFilter | null;
  onChange: (filter: SourceFieldFilter | null) => void;
  disabled?: boolean;
  error?: string;
}

const SourceFieldFilterBuilder: React.FC<SourceFieldFilterBuilderProps> = ({
  sourceFielddefId,
  tenantId,
  value,
  onChange,
  disabled = false,
  error,
}) => {
  const [relatedFields, setRelatedFields] = useState<RelatedFieldDefinition[]>([]);
  // Initialize loading to true if sourceFielddefId is provided to prevent MUI warnings
  const [loading, setLoading] = useState(!!sourceFielddefId);
  const [validationError, _setValidationError] = useState<string | null>(null);

  // Load related fields when source field changes
  useEffect(() => {
    const loadRelatedFields = async () => {
      if (!sourceFielddefId) {
        setRelatedFields([]);
        return;
      }

      setLoading(true);
      try {
        const fields = await metadataService.getRelatedFields(sourceFielddefId, tenantId);
        setRelatedFields(fields);
      } catch (err) {
        console.error('Failed to load related fields:', err);
        setRelatedFields([]);
      } finally {
        setLoading(false);
      }
    };

    loadRelatedFields();
  }, [sourceFielddefId, tenantId]);

  const handleAddCondition = () => {
    const newCondition: FilterCondition = {
      related_fielddef_id: '',
      operator: '=',
      value: '',
    };

    const updatedFilter: SourceFieldFilter = value
      ? {
          ...value,
          conditions: [...value.conditions, newCondition],
        }
      : {
          operator: 'AND',
          conditions: [newCondition],
        };

    onChange(updatedFilter);
  };

  const handleRemoveCondition = (index: number) => {
    if (!value) return;

    const updatedConditions = value.conditions.filter((_, i) => i !== index);

    if (updatedConditions.length === 0) {
      onChange(null);
    } else {
      onChange({
        ...value,
        conditions: updatedConditions,
      });
    }
  };

  const handleConditionChange = (index: number, field: keyof FilterCondition, val: any) => {
    if (!value) return;

    const updatedConditions = [...value.conditions];
    updatedConditions[index] = {
      ...updatedConditions[index],
      [field]: val,
    };

    onChange({
      ...value,
      conditions: updatedConditions,
    });
  };

  const handleOperatorChange = (newOperator: 'AND' | 'OR') => {
    if (!value) return;
    onChange({
      ...value,
      operator: newOperator,
    });
  };

  const requiresValue = (operator: FilterOperator): boolean => {
    return operator !== 'IS NULL' && operator !== 'IS NOT NULL';
  };

  const getValueInput = (
    condition: FilterCondition,
    index: number,
    relatedField?: RelatedFieldDefinition
  ) => {
    // For IN operator, use multi-select
    if (condition.operator === 'IN') {
      return (
        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={Array.isArray(condition.value) ? condition.value : []}
          onChange={(_, newValue) => {
            handleConditionChange(index, 'value', newValue);
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, idx) => (
              <Chip label={option} {...getTagProps({ index: idx })} key={idx} />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Values"
              placeholder="Type and press Enter"
              helperText="Enter multiple values (one per tag)"
            />
          )}
          disabled={disabled}
          fullWidth
        />
      );
    }

    // For other operators requiring a value
    if (requiresValue(condition.operator)) {
      return (
        <TextField
          label="Value"
          value={condition.value || ''}
          onChange={(e) => {
            handleConditionChange(index, 'value', e.target.value);
          }}
          disabled={disabled}
          fullWidth
          type={
            relatedField?.datatype === 'decimal' || relatedField?.datatype === 'currency'
              ? 'number'
              : 'text'
          }
        />
      );
    }

    return null;
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Operator selector */}
      {value && value.conditions.length > 1 && (
        <Box sx={{ mb: 2 }}>
          <FormControl size="small">
            <InputLabel>Combine Conditions</InputLabel>
            <Select
              value={value.operator}
              onChange={(e) => handleOperatorChange(e.target.value as 'AND' | 'OR')}
              label="Combine Conditions"
              disabled={disabled}
            >
              <MenuItem value="AND">Match ALL conditions (AND)</MenuItem>
              <MenuItem value="OR">Match ANY condition (OR)</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Conditions */}
      {value?.conditions.map((condition, index) => {
        const relatedField = relatedFields.find(
          (rf) => rf.fielddef_id === condition.related_fielddef_id
        );

        return (
          <Box
            key={index}
            sx={{
              p: 2,
              mb: 2,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              {/* Related Field Selector */}
              <FormControl sx={{ flex: 1 }}>
                <InputLabel id={`field-label-${index}`}>Field</InputLabel>
                <Select
                  labelId={`field-label-${index}`}
                  id={`field-select-${index}`}
                  value={
                    condition.related_fielddef_id &&
                    relatedFields.some((rf) => rf.fielddef_id === condition.related_fielddef_id)
                      ? condition.related_fielddef_id
                      : ''
                  }
                  onChange={(e) => {
                    handleConditionChange(index, 'related_fielddef_id', e.target.value);
                  }}
                  label="Field"
                  disabled={disabled || loading}
                  error={!condition.related_fielddef_id}
                >
                  {relatedFields.map((rf) => (
                    <MenuItem key={rf.fielddef_id} value={rf.fielddef_id}>
                      {rf.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Operator Selector */}
              <FormControl sx={{ flex: 1 }}>
                <InputLabel id={`operator-label-${index}`}>Operator</InputLabel>
                <Select
                  labelId={`operator-label-${index}`}
                  id={`operator-select-${index}`}
                  value={condition.operator || ''}
                  onChange={(e) => {
                    if (!value) return;

                    // Get the raw value from the event - ensure it's a string but preserve spaces
                    const rawValue = e.target.value;
                    // Convert to string but don't trim (preserve spaces for "IS NULL" and "IS NOT NULL")
                    const selectedValue = String(rawValue);
                    const newOperator = selectedValue as FilterOperator;
                    const oldOperator = condition.operator;

                    // Determine if we need to clear the value
                    const shouldClearValue =
                      !requiresValue(newOperator) || !requiresValue(oldOperator);

                    // Update both operator and value in a single state update
                    const updatedConditions = [...value.conditions];
                    updatedConditions[index] = {
                      ...updatedConditions[index],
                      operator: newOperator,
                      ...(shouldClearValue ? { value: undefined } : {}),
                    };

                    onChange({
                      ...value,
                      conditions: updatedConditions,
                    });
                  }}
                  label="Operator"
                  disabled={disabled}
                  // Force re-render when these specific values are selected to fix Material-UI bug
                  key={`operator-select-${index}-${condition.operator || 'empty'}`}
                  MenuProps={{
                    // Ensure menu items are properly selectable
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                  }}
                >
                  <MenuItem value="=">=</MenuItem>
                  <MenuItem value="!=">!=</MenuItem>
                  <MenuItem value="IN">IN (multiple values)</MenuItem>
                  <MenuItem value=">">{'>'}</MenuItem>
                  <MenuItem value=">=">{'>='}</MenuItem>
                  <MenuItem value="<">{'<'}</MenuItem>
                  <MenuItem value="<=">{'<='}</MenuItem>
                  <MenuItem value="IS NULL">IS NULL</MenuItem>
                  <MenuItem value="IS NOT NULL">IS NOT NULL</MenuItem>
                </Select>
              </FormControl>

              {/* Value Input (conditional) */}
              <Box sx={{ flex: 1 }}>{getValueInput(condition, index, relatedField)}</Box>

              {/* Delete Button */}
              <IconButton
                onClick={() => handleRemoveCondition(index)}
                disabled={disabled}
                color="error"
                aria-label="Remove condition"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          </Box>
        );
      })}

      {/* Add Condition Button */}
      <Button
        startIcon={<AddIcon />}
        onClick={handleAddCondition}
        disabled={disabled || loading || !relatedFields.length}
        variant="outlined"
        size="small"
      >
        Add Condition
      </Button>

      {/* No related fields warning */}
      {!loading && relatedFields.length === 0 && sourceFielddefId && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          The selected source field has no related fields. Add related fields to the source field
          definition to enable filtering.
        </Alert>
      )}

      {/* Validation error */}
      {(error || validationError) && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error || validationError}
        </Alert>
      )}

      {/* Filter Preview */}
      {value && value.conditions.length > 0 && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="caption" color="textSecondary">
            Filter Preview:
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {value.conditions.map((condition, index) => {
              const relatedField = relatedFields.find(
                (rf) => rf.fielddef_id === condition.related_fielddef_id
              );
              const fieldLabel = relatedField?.label || condition.related_fielddef_id;

              let valueDisplay = '';
              if (condition.operator === 'IN' && Array.isArray(condition.value)) {
                valueDisplay = `[${condition.value.join(', ')}]`;
              } else if (requiresValue(condition.operator)) {
                valueDisplay = String(condition.value || '');
              }

              const conditionText = requiresValue(condition.operator)
                ? `${fieldLabel} ${condition.operator} ${valueDisplay}`
                : `${fieldLabel} ${condition.operator}`;

              return (
                <React.Fragment key={index}>
                  {index > 0 && ` ${value.operator} `}
                  {conditionText}
                </React.Fragment>
              );
            })}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SourceFieldFilterBuilder;
